import React, { useEffect, useState } from 'react';
import { MessageCircle, Mail, Send, LifeBuoy } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { fetchFaqs, submitSupportTicket } from '@/lib/api';
import { useApp } from '@/state/AppContext';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';

const SupportPage = () => {
  const { config } = useApp();
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', order_id: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { fetchFaqs().then(setFaqs).catch(() => {}); }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // RFC-5322 lite email regex — accepts virtually all real-world addresses,
  // rejects obviously bad input like "foo", "foo@", "foo@bar", "@baz".
  const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const submit = async () => {
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Fill name, email, subject and message');
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error('Enter a valid email', { description: 'e.g. name@domain.com' });
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error('Message is a bit short', { description: 'Please add a few more details so we can help.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitSupportTicket({ ...form, email: form.email.trim() });
      setSuccess(true);
      toast.success('Sent! We’ll reply within 24h.');
    } catch (e) {
      toast.error('Could not send', { description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const waMsg = encodeURIComponent('Hi Big Bash support, I need help with my order.');

  return (
    <div data-testid="support-page">
      <section className="bb-block-yellow">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 text-[var(--bb-darkblue)]">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-darkblue)] text-white text-xs font-nunito-black uppercase tracking-wider">
            <LifeBuoy className="w-4 h-4" /> We're here for you
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95] text-[var(--bb-darkblue)]">
            Customer Support
          </h1>
          <p className="text-sm md:text-base text-[var(--bb-darkblue)]/90 mt-3 font-nunito-medium max-w-xl">
            We're here to make sure every bite is sweet. Reach out on WhatsApp, email, or send us a message below.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/15 p-5">
          <h2 className="font-display text-2xl text-[var(--bb-darkblue)]">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-2">
            {faqs.map((f, idx) => (
              <AccordionItem key={f.id || idx} value={f.id || `faq-${idx}`}>
                <AccordionTrigger className="text-sm font-nunito-black text-left text-[var(--bb-darkblue)]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm font-nunito-medium text-[var(--bb-darkgrey)]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <aside className="space-y-3">
          <a href={`https://wa.me/${config.whatsapp_number}?text=${waMsg}`} target="_blank" rel="noreferrer" className="block">
            <div className="rounded-2xl bb-block-cyan p-4 flex items-center gap-3 border-2 border-[var(--bb-darkblue)] hover:-translate-y-0.5 transition-transform" data-testid="whatsapp-support-button">
              <MessageCircle className="w-6 h-6 text-white" />
              <div className="flex-1 text-white">
                <div className="text-sm font-nunito-black">Chat on WhatsApp</div>
                <div className="text-xs text-white/90 font-nunito-medium">Fastest way to reach us · 9am-9pm</div>
              </div>
            </div>
          </a>
          <a href={`mailto:${config.support_email}`} className="block">
            <div className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/15 p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform">
              <Mail className="w-6 h-6 text-[var(--bb-pink)]" />
              <div className="flex-1">
                <div className="text-sm font-nunito-black text-[var(--bb-darkblue)]">Email us</div>
                <div className="text-xs text-[var(--bb-darkgrey)] font-nunito-medium">{config.support_email}</div>
              </div>
            </div>
          </a>
        </aside>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6">
        {success ? (
          <div className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/15 p-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-[var(--bb-cyan-soft)] flex items-center justify-center">
              <Send className="w-5 h-5 text-[var(--bb-cyan)]" />
            </div>
            <h2 className="font-display text-2xl text-[var(--bb-darkblue)] mt-2">Thanks! We got your message.</h2>
            <p className="text-sm text-[var(--bb-darkgrey)] font-nunito-medium">Our team will reply within 24 hours.</p>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/15 p-5">
            <h2 className="font-display text-2xl text-[var(--bb-darkblue)]">Or send us a message</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs font-nunito-black">Name *</Label>
                <Input value={form.name} onChange={update('name')} className="h-11 rounded-xl mt-1 border-2" data-testid="support-name" />
              </div>
              <div>
                <Label className="text-xs font-nunito-black">Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={!!form.email && !EMAIL_RE.test(form.email.trim())}
                  className="h-11 rounded-xl mt-1 border-2 aria-[invalid=true]:border-[var(--bb-pink)]"
                  data-testid="support-email"
                />
                {!!form.email && !EMAIL_RE.test(form.email.trim()) && (
                  <div className="mt-1 text-[11px] font-nunito-bold text-[var(--bb-pink)]" data-testid="support-email-error">
                    Please enter a valid email address.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-nunito-black">Phone</Label>
                <Input value={form.phone} onChange={update('phone')} className="h-11 rounded-xl mt-1 border-2" data-testid="support-phone" />
              </div>
              <div>
                <Label className="text-xs font-nunito-black">Order ID (optional)</Label>
                <Input value={form.order_id} onChange={update('order_id')} placeholder="BB-XXXXXX" className="h-11 rounded-xl mt-1 border-2" data-testid="support-order-id" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-nunito-black">Subject *</Label>
                <Input value={form.subject} onChange={update('subject')} className="h-11 rounded-xl mt-1 border-2" data-testid="support-subject" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-nunito-black">Message *</Label>
                <Textarea value={form.message} onChange={update('message')} rows={4} className="rounded-xl mt-1 border-2" data-testid="support-message" />
              </div>
            </div>
            <Button onClick={submit} disabled={submitting} className="mt-4 h-12 px-5 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black" data-testid="support-submit">
              <Send className="w-4 h-4 mr-1.5" /> {submitting ? 'Sending…' : 'Send message'}
            </Button>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
